(() => {
  const runtimeKey = "__jscaHeaderRuntime";

  if (window[runtimeKey]) {
    window[runtimeKey].scan(document);
    return;
  }

  class JSCAHeader {
    constructor(root) {
      this.root = root;
      this.nav = root.querySelector("[data-nav]");
      this.abortController = new AbortController();
      this.timer = 0;
      this.drawerTimers = new Map();
      this.openDialog = null;
      this.dialogOpener = new WeakMap();
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.scrollFrame = 0;
      this.lastDecisionY = window.scrollY;
      this.initAnnouncements();
      this.initInfo();
      this.initDrawers();
      this.initScrollBehavior();
      this.initMegaMenuKeyboard();
    }

    on(target, type, listener, options = {}) {
      if (!target) return;
      target.addEventListener(type, listener, {
        ...options,
        signal: this.abortController.signal,
      });
    }

    initAnnouncements() {
      this.announcementRegion = this.root.querySelector("[data-announcements]");
      this.announcements = Array.from(this.root.querySelectorAll("[data-announcement]"));
      this.announcementIndex = Math.max(
        0,
        this.announcements.findIndex((announcement) => announcement.classList.contains("is-active")),
      );
      this.announcementPaused = false;
      this.autoRotate = this.root.dataset.autoRotate === "true";

      if (!this.announcementRegion || this.announcements.length === 0) return;

      this.showAnnouncement(this.reducedMotion.matches ? 0 : this.announcementIndex);
      this.on(this.announcementRegion, "mouseenter", () => this.pauseAnnouncements());
      this.on(this.announcementRegion, "mouseleave", () => this.resumeAnnouncements());
      this.on(this.announcementRegion, "focusin", () => this.pauseAnnouncements());
      this.on(this.announcementRegion, "focusout", () => {
        queueMicrotask(() => {
          if (!this.announcementRegion.contains(document.activeElement)) this.resumeAnnouncements();
        });
      });
      this.on(this.reducedMotion, "change", (event) => {
        window.clearTimeout(this.timer);
        if (event.matches) this.showAnnouncement(0);
        else this.scheduleAnnouncement();
      });
      this.scheduleAnnouncement();
    }

    showAnnouncement(index) {
      this.announcementIndex = index;
      this.announcements.forEach((announcement, announcementIndex) => {
        const active = announcementIndex === index;
        announcement.classList.toggle("is-active", active);
        announcement.setAttribute("aria-hidden", String(!active));
      });
    }

    scheduleAnnouncement() {
      window.clearTimeout(this.timer);
      if (
        !this.autoRotate ||
        this.announcementPaused ||
        this.reducedMotion.matches ||
        this.announcements.length < 2
      ) {
        return;
      }

      const delay = Math.max(
        1000,
        Number(this.announcements[this.announcementIndex]?.dataset.delay) || 7000,
      );
      this.timer = window.setTimeout(() => {
        this.showAnnouncement((this.announcementIndex + 1) % this.announcements.length);
        this.scheduleAnnouncement();
      }, delay);
    }

    pauseAnnouncements() {
      this.announcementPaused = true;
      window.clearTimeout(this.timer);
    }

    resumeAnnouncements() {
      this.announcementPaused = false;
      this.scheduleAnnouncement();
    }

    initInfo() {
      this.info = this.root.querySelector("[data-info]");
      this.infoToggle = this.root.querySelector("[data-info-toggle]");
      this.infoPanel = this.root.querySelector("[data-info-panel]");
      if (!this.info || !this.infoToggle || !this.infoPanel) return;

      let pointerOpenState = false;
      let pointerDown = false;
      let suppressFocusOpen = false;
      const hoverPointer = window.matchMedia("(hover: hover) and (pointer: fine)");

      this.on(this.info, "pointerenter", () => {
        if (hoverPointer.matches) this.openInfo();
      });
      this.on(this.info, "pointerleave", () => {
        if (hoverPointer.matches && !this.info.contains(document.activeElement)) {
          this.closeInfo();
        }
      });

      this.on(this.infoToggle, "pointerdown", () => {
        pointerDown = true;
        pointerOpenState = this.info.classList.contains("is-open");
      });
      this.on(this.infoToggle, "focus", () => {
        if (!pointerDown && !suppressFocusOpen) this.openInfo();
      });
      this.on(this.infoToggle, "click", (event) => {
        event.preventDefault();
        if (pointerDown) {
          pointerOpenState ? this.closeInfo() : this.openInfo();
          pointerDown = false;
          return;
        }
        this.info.classList.contains("is-open") ? this.closeInfo() : this.openInfo();
      });
      this.on(document, "pointerdown", (event) => {
        if (!this.info.contains(event.target)) this.closeInfo();
      });
      this.on(document, "focusin", (event) => {
        if (!this.info.contains(event.target)) this.closeInfo();
      });
      this.on(document, "keydown", (event) => {
        if (event.key !== "Escape" || !this.info.classList.contains("is-open")) return;
        event.preventDefault();
        suppressFocusOpen = true;
        this.closeInfo();
        this.infoToggle.focus();
        queueMicrotask(() => {
          suppressFocusOpen = false;
        });
      });
    }

    openInfo() {
      if (!this.infoPanel) return;
      this.infoPanel.hidden = false;
      this.info.classList.add("is-open");
      this.infoToggle.setAttribute("aria-expanded", "true");
    }

    closeInfo() {
      if (!this.infoPanel) return;
      this.infoPanel.hidden = true;
      this.info.classList.remove("is-open");
      this.infoToggle.setAttribute("aria-expanded", "false");
    }

    initDrawers() {
      this.dialogs = Array.from(this.root.querySelectorAll("[data-drawer]"));
      this.triggers = Array.from(this.root.querySelectorAll("[data-drawer-open]"));
      const mobileMenuViewport = window.matchMedia("(max-width: 1023px)");

      this.triggers.forEach((trigger) => {
        this.on(trigger, "click", (event) => {
          if (trigger.dataset.drawerOpen === "menu" && !mobileMenuViewport.matches) return;
          const dialog = this.dialogs.find(
            (candidate) => candidate.dataset.drawer === trigger.dataset.drawerOpen,
          );
          if (!dialog) return;
          event.preventDefault();
          this.openDrawer(dialog, trigger);
        });
      });

      const menuDialog = this.dialogs.find((dialog) => dialog.dataset.drawer === "menu");
      this.on(mobileMenuViewport, "change", (event) => {
        if (!event.matches && menuDialog?.open) this.closeDrawer(menuDialog, true, false);
      });

      this.dialogs.forEach((dialog) => {
        this.on(dialog, "click", (event) => {
          const link = event.target.closest("a[href]");
          if (link && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && link.target !== "_blank") {
            this.closeDrawer(dialog, true, false);
          }
        });
        dialog.querySelectorAll("[data-drawer-close]").forEach((closer) => {
          this.on(closer, "click", () => this.closeDrawer(dialog));
        });
        this.on(dialog, "cancel", (event) => {
          event.preventDefault();
          this.closeDrawer(dialog);
        });
        this.on(dialog, "close", () => this.finishDrawerClose(dialog));
      });
    }

    openDrawer(dialog, opener) {
      if (this.openDialog && this.openDialog !== dialog) this.closeDrawer(this.openDialog, true, false);
      if (dialog.open) return;

      const pendingTimer = this.drawerTimers.get(dialog);
      if (pendingTimer) window.clearTimeout(pendingTimer);
      this.dialogOpener.set(dialog, opener);
      opener.setAttribute("aria-expanded", "true");
      dialog.classList.remove("is-closing");
      dialog.showModal();
      this.openDialog = dialog;
      document.body.classList.add("jsca-header-lock");

      requestAnimationFrame(() => {
        dialog.classList.add("is-open");
        const focusTarget =
          dialog.querySelector("[data-search-input]") ||
          dialog.querySelector("summary, a[href], button:not([data-drawer-close])") ||
          dialog.querySelector("button");
        requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
      });
    }

    closeDrawer(dialog, immediate = false, restoreFocus = true) {
      if (!dialog?.open) return;
      const duration = immediate || this.reducedMotion.matches ? 0 : 700;
      dialog.dataset.restoreFocus = String(restoreFocus);
      dialog.classList.remove("is-open");
      dialog.classList.add("is-closing");
      const existingTimer = this.drawerTimers.get(dialog);
      if (existingTimer) window.clearTimeout(existingTimer);
      const timer = window.setTimeout(() => {
        this.drawerTimers.delete(dialog);
        if (dialog.open) dialog.close();
      }, duration);
      this.drawerTimers.set(dialog, timer);
    }

    finishDrawerClose(dialog) {
      const timer = this.drawerTimers.get(dialog);
      if (timer) window.clearTimeout(timer);
      this.drawerTimers.delete(dialog);
      dialog.classList.remove("is-open", "is-closing");
      const opener = this.dialogOpener.get(dialog);
      opener?.setAttribute("aria-expanded", "false");
      if (this.openDialog === dialog) this.openDialog = null;
      if (!document.querySelector(".jsca-drawer[open]")) {
        document.body.classList.remove("jsca-header-lock");
      }
      if (dialog.dataset.restoreFocus !== "false" && opener?.isConnected) {
        opener.focus({ preventScroll: true });
      }
      delete dialog.dataset.restoreFocus;
    }

    initScrollBehavior() {
      if (!this.nav) return;
      this.on(this.nav, "focusin", () => this.nav.classList.remove("is-hidden"));

      const announcementRow = this.root.querySelector("[data-announcement-row]");
      const syncAnnouncementHeight = () => {
        const height = announcementRow?.getBoundingClientRect().height || 0;
        this.root.style.setProperty("--announcement-height", `${height}px`);
        this.nav.style.setProperty("--announcement-height", `${height}px`);
        return height;
      };

      syncAnnouncementHeight();
      if (this.root.dataset.stickyMode === "static") return;

      const update = () => {
        this.scrollFrame = 0;
        const scrollY = Math.max(0, window.scrollY);
        const announcementHeight = syncAnnouncementHeight();
        const hasAnnouncementRow = Boolean(announcementRow);

        this.nav.classList.toggle(
          "is-past-announcement",
          hasAnnouncementRow && scrollY > announcementHeight,
        );
        if (scrollY <= announcementHeight) this.nav.classList.remove("is-hidden");

        const distance = scrollY - this.lastDecisionY;
        if (Math.abs(distance) >= 10) {
          const smart = this.root.dataset.stickyMode === "smart";
          const canHide =
            smart &&
            scrollY > announcementHeight + 100 &&
            !this.nav.matches(":hover") &&
            !this.nav.contains(document.activeElement) &&
            !this.openDialog;

          if (distance > 0 && canHide) this.nav.classList.add("is-hidden");
          if (distance < 0 || !smart) this.nav.classList.remove("is-hidden");
          this.lastDecisionY = scrollY;
        }
      };

      const requestUpdate = () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(update);
      };

      this.on(window, "scroll", requestUpdate, { passive: true });
      this.on(window, "resize", requestUpdate, { passive: true });
      update();
    }

    initMegaMenuKeyboard() {
      const menuItems = Array.from(
        this.root.querySelectorAll(".jsca-header__desktop-item.has-mega"),
      );

      menuItems.forEach((menuItem) => {
        const topLevelLink = menuItem.querySelector(":scope > .jsca-header__desktop-link");
        const reopen = () => menuItem.classList.remove("is-escape-closed");

        this.on(menuItem, "pointerenter", reopen);
        this.on(menuItem, "pointerleave", () => {
          if (!menuItem.contains(document.activeElement)) reopen();
        });
        this.on(menuItem, "focusout", () => {
          queueMicrotask(() => {
            if (!menuItem.contains(document.activeElement)) reopen();
          });
        });
        this.on(topLevelLink, "focusin", reopen);
        this.on(topLevelLink, "pointerdown", reopen);
      });

      this.on(document, "keydown", (event) => {
        if (event.key !== "Escape" || event.defaultPrevented || this.openDialog) return;
        let menuItem = document.activeElement?.closest(".jsca-header__desktop-item.has-mega");
        if (!menuItem || !this.root.contains(menuItem)) {
          menuItem = menuItems.find((item) => item.matches(":hover"));
        }
        if (!menuItem) return;
        const topLevelLink = menuItem.querySelector(":scope > .jsca-header__desktop-link");
        if (!topLevelLink) return;
        event.preventDefault();
        topLevelLink.focus({ preventScroll: true });
        menuItem.classList.add("is-escape-closed");
      });
    }

    destroy() {
      window.clearTimeout(this.timer);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.drawerTimers.forEach((timer) => window.clearTimeout(timer));
      this.drawerTimers.clear();
      this.dialogs?.forEach((dialog) => {
        if (dialog.open) {
          dialog.dataset.restoreFocus = "false";
          dialog.close();
        }
      });
      this.abortController.abort();
      if (!document.querySelector(".jsca-drawer[open]")) {
        document.body.classList.remove("jsca-header-lock");
      }
      delete this.root.__jscaHeaderController;
    }
  }

  const controllers = new WeakMap();

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches("[data-jsca-header]")) roots.push(scope);
    scope.querySelectorAll?.("[data-jsca-header]").forEach((root) => roots.push(root));
    return roots;
  };

  const scan = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      controllers.get(root)?.destroy();
      const controller = new JSCAHeader(root);
      controllers.set(root, controller);
      root.__jscaHeaderController = controller;
    });
  };

  const runtime = { scan };
  window[runtimeKey] = runtime;

  document.addEventListener("shopify:section:load", (event) => scan(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    rootsWithin(event.target).forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
